import React, { useEffect, useMemo, useState } from 'react';
import DetailSection from '@components/misc/detail/DetailSection';
import ProfilePicture from '@components/profile/ProfilePicture';
import DetailItem from '@components/misc/detail/DetailItem';
import { useNavigate } from 'react-router-dom';
import { useSchoolDetails } from '@hooks/user.hook';
import { USER_GENDER_MALE } from '@utils/constants/user.constants';
import { IFormAcademicData } from '@models/interfaces/user.interface';
import { levelName } from '@utils/helpers/levelName';
import { useAtomValue } from 'jotai';
import { userDetailQueryAtom } from '@atom/globalQueryAtom';

interface IAcademicThumbData {
  school_name: string;
  level_name: string;
}

interface IBasicThumbData {
  username: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  show_date_of_birth: number;
  gender: string;
  user_id: number;
  profile_picture: string;
  pp_ext: string;
}

const ProfilePageContent = () => {
  const navigate = useNavigate();
  const getUserDetails = useAtomValue(userDetailQueryAtom);
  const [academicThumbData, setAcademicThumbData] =
    useState<IAcademicThumbData>({
      school_name: '',
      level_name: '',
    });
  const [academicData, setAcademicData] = useState<IFormAcademicData>({
    school_id: 0,
    show_school: 1,
    level_id: 0,
    class: '0',
  });

  const [userDetail, setUserDetail] = useState<IBasicThumbData>({
    username: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    show_date_of_birth: 0,
    gender: '',
    user_id: 0,
    profile_picture: '',
    pp_ext: '',
  });

  const userLevelName = useMemo(() => {
    const userData = getUserDetails.data?.data;
    if (!getUserDetails.isSuccess || !userData) return '';

    return levelName(Math.abs(parseInt(userData.level_id)), false) ?? '';
  }, [getUserDetails.isSuccess, getUserDetails.data?.data]);

  useEffect(() => {
    if (getUserDetails.isSuccess) {
      const userData = getUserDetails?.data?.data;

      if (userData) {
        setUserDetail({
          ...userDetail,
          username: userData.username,
          first_name: userData.first_name,
          last_name: userData.last_name,
          date_of_birth: userData.dob,
          show_date_of_birth: userData.show_date_of_birth,
          gender: userData.gender,
          user_id: parseInt(userData.user_id),
          profile_picture: userData.pp,
          pp_ext: userData.pp_ext,
        });

        setAcademicData({
          ...academicData,
          school_id: parseInt(userData.school_id),
          show_school: userData.show_school,
          level_id: parseInt(userData.level_id),
          class: userData.class,
        });

        setAcademicThumbData({
          ...academicThumbData,
        });
      }
    }
  }, [getUserDetails.data]);

  const schoolDetail = useSchoolDetails(academicData.school_id);

  useEffect(() => {
    if (schoolDetail.isSuccess) {
      setAcademicThumbData({
        ...academicThumbData,
        school_name: schoolDetail.data.data.data.school_name,
      });
    }
  }, [schoolDetail.data]);

  function handleOnEditBasicInfo() {
    navigate('/profile/update-basic-info');
  }

  function handleOnEditAcademic() {
    navigate('/profile/update-academic');
  }

  const formatDate = (dateofBirth: string) => {
    let date = new Date(dateofBirth);

    const day = date.toLocaleString('default', { day: '2-digit' });
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.toLocaleString('default', { year: 'numeric' });

    return day + ' ' + month + ' ' + year;
  };

  return (
    <>
      <div
        className={
          'text-neutral-whiteboard text-lg mb-4 font-semibold max-lg:hidden'
        }
      >
        Profile
      </div>
      <div className={'mb-6'}>
        {!getUserDetails.isLoading ? (
          <ProfilePicture
            name={userDetail.first_name + ' ' + userDetail.last_name}
            studentId={userDetail.user_id}
            imageUrl={userDetail.profile_picture}
            pp_ext={userDetail.pp_ext}
            imageEdited={onResolve => {
              // refresh the user detail
              getUserDetails.refetch().then(() => {
                onResolve();
              });
            }}
            isLoading={getUserDetails.isLoading}
          />
        ) : (
          <></>
        )}
      </div>
      <DetailSection
        title={'Basic Info'}
        onEdit={handleOnEditBasicInfo}
        className={'mb-8'}
      >
        <DetailItem
          label={'Username'}
          value={userDetail.username}
          className={'mb-6'}
        />
        {Number(userDetail.show_date_of_birth) ? (
          <DetailItem
            label={'Date of Birth'}
            value={
              !isNaN(Date.parse(userDetail.date_of_birth))
                ? formatDate(userDetail.date_of_birth)
                : ''
            }
            className={'mb-6'}
          />
        ) : (
          <></>
        )}
        {userDetail.gender ? (
          <DetailItem
            label={'Gender'}
            value={userDetail.gender == USER_GENDER_MALE ? 'Male' : 'Female'}
          />
        ) : (
          <></>
        )}
      </DetailSection>
      <DetailSection title={'Academic'} onEdit={handleOnEditAcademic}>
        {Number(academicData.show_school) ? (
          <DetailItem
            label={'School'}
            value={academicThumbData.school_name}
            className={'mb-6'}
          />
        ) : (
          <></>
        )}
        <DetailItem label={'Level'} value={userLevelName} className={'mb-6'} />
        <DetailItem label={'Class'} value={academicData.class} />
      </DetailSection>
    </>
  );
};

export default ProfilePageContent;
